import { MessageDescriptor } from 'react-intl'

import { addMonths, differenceInYears, isBefore, parseISO } from 'date-fns'
import { cloneDeep, intersection, isNil, isNumber, omit, partition, pick, sum, uniq } from 'lodash-es'
import moment from 'moment-timezone'

import {
  AgeBucket,
  ageBuckets,
  allChildPrimaryNeedsWordCloudMappings,
  ContactCareNavMessageSearcherType,
  ContactUsFormValues,
  currentToNextMetadataNamesAll,
  currentToNextMetadataNamesDefault,
  PrimaryNeedsWords,
  SearcherForTriageSummary,
  TriageScreenerField,
  TriageScreenerValue,
  TriageWords,
} from './constants'
import { ProgramNames } from '../../models/programs/Programs'
import {
  PROVIDER_TYPES,
  ProviderCapacity,
  ProviderInfo,
  ProviderProgramTaxonomy,
  ProviderType,
  TreatmentModeEnums,
} from '../../models/providers/Providers'
import {
  ExcludedReason,
  GetWordCloudWordsActionData,
  MetadataContext,
  SearcherType,
  SearchRequestData,
  TriageResults,
  TriageRiskLevel,
  TriageSearch,
  WordCloudCategory,
  WordCloudData,
  WordCloudWords,
} from '../../models/searchForCare/SearchForCare'
import { TriageScreenerNames, TriageScreener as TriageScreenerType } from '../../models/triageScreener/TriageScreener'
import { ContactPref, Customer, User } from '../../models/user/User'
import {
  NeurodiversityContactCareNavigatorEntryPoint,
  NeurodiversitySearcherInfo,
  NeurodiversitySearcherType,
  TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
} from '../centerOfExcellence/types'
import { getToolkitSubTopicLabel } from '../centerOfExcellence/utils'
import { UnsupportedTreatmentOptionsConfigurationsType } from '../common/constants/baseTreatmentOptionConstants'
import { EligibilityType } from '../common/constants/constants'
import { CustomerName } from '../common/constants/customer'
import { PRODUCT_TYPE } from '../common/constants/productType'
import {
  tierOnePrimaryTreatmentOptionsToDisplay,
  tierOneSecondaryTreatmentOptionsToDisplay,
  TREATMENT_OPTIONS,
  TREATMENT_OPTIONS_TYPE,
} from '../common/constants/treatmentOptions'
import { TREATMENT_RECOMMENDATION_TIER } from '../common/constants/treatmentRecommendationsConstants'
import { isWithinPastTimeFrame, isWithinXHoursInThePast, TimeUnit } from '../common/utils/dateUtils'
import { isAgeTeen, isAgeYoungerThanTeen } from '../common/utils/userUtils'
import { S4C_PROGRAM_OPTION_FLOW } from '../memberPreferences/constants'
import { Programs } from '../programConfigs/programConfigs'
import { ProgramConfig } from '../programConfigs/types'
import { getProviderSupportsInPerson, isBlendedCareProvider } from '../providers/utils'
import {
  getToolkitSubTopicDescriptions,
  getToolkitTopicDescriptions,
  PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG,
  PRIMARY_NEEDS_WORDS_ADULT_MSG,
  PRIMARY_NEEDS_WORDS_CHILD_MSG,
  TRIAGE_WORDS_MSG,
} from '../searchForCare/messages'
import { DEFAULT_SPECULATIVE_FILTER_THRESHOLDS } from '../wellnessToolkit/constants'
import { TOOLKIT_TYPE, WELLNESS_TOOLKIT_TYPE } from '../wellnessToolkit/types'

type AgeUtilArgs = {
  date: string
  ageArg?: number
  dateFormat?: string
}

export type UseNewSearchDataArgs = {
  dob?: string
  userId?: string
  gender?: string
  employer?: string
  type?: SearcherType
  childUserId?: string
  productType: PRODUCT_TYPE
  toolkitType?: TOOLKIT_TYPE
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  eligibilityType?: EligibilityType
  countryName?: string
}

const NUMBER_OF_MONTHS_UNTIL_SURVEY_CAN_BE_SHOWN_AGAIN = 3
const WELLNESS_SUB_TOPIC_SUFFIX = '-sub-topic'

export const getAge = ({ date, dateFormat = 'MM/DD/YYYY' }: AgeUtilArgs) => {
  const momentDate = moment(date, dateFormat)
  if (!momentDate.isValid()) {
    return NaN
  }

  const age = moment().diff(moment(momentDate), 'years')
  return age
}

export function getAgeISO(iso8601Date: string): number {
  return differenceInYears(new Date(), parseISO(iso8601Date))
}

export const getAgeBucket = ({ date, dateFormat = 'MM/DD/YYYY', ageArg }: AgeUtilArgs): AgeBucket | undefined => {
  let age: number
  if (ageArg) {
    age = ageArg
  } else {
    age = getAge({ date, dateFormat })
  }
  return ageBuckets.find((ageBucket) => age >= ageBucket.min && age <= ageBucket.max)
}

export function getAgeBucketISO(iso8601Date: string): AgeBucket | undefined {
  const age = getAgeISO(iso8601Date)
  return ageBuckets.find((ageBucket) => age >= ageBucket.min && age <= ageBucket.max)
}

export const getIsCareBeingSearchedForTeen = ({
  isOutsideOnboarding,
  isUserTeen,
  isSearcherTeen,
}: {
  isOutsideOnboarding: boolean
  isUserTeen: boolean
  isSearcherTeen: boolean
}) => {
  return isOutsideOnboarding ? isUserTeen : isSearcherTeen
}

export const getIsParentLedLctTeenBooking = ({
  isLyraCareProvider,
  isChildSearch,
  isSearcherTeen,
}: {
  isLyraCareProvider: boolean
  isChildSearch: boolean // true if a guardian is searching for care for a child.
  isSearcherTeen: boolean // true if care is being searched for a teen. Can be true if the user is a teen searching for self or if a guardian is searching for care for a child who is a teen.
}) => {
  return isLyraCareProvider && isChildSearch && isSearcherTeen
}

export const getNewSearchData = ({
  dob,
  userId,
  gender,
  employer,
  type,
  childUserId,
  productType,
  toolkitType,
  toolkitSubTopic,
  eligibilityType,
  countryName,
}: UseNewSearchDataArgs) => {
  const age = dob ? getAge({ date: dob }) : null

  const yob = dob ? moment(dob, 'MM/DD/YYYY').year() : null
  const topicBody = toolkitType ? { clientSelectedTopic: getClientSelectedTopicKey(toolkitType) } : {}
  const subTopicBody =
    toolkitType && toolkitSubTopic ? { clientSelectedSubTopic: getClientSelectedSubTopicKey(toolkitSubTopic) } : {}
  const eligibilityTypeBody = eligibilityType ? { eligibility_type: eligibilityType } : {}

  if (!!userId && !isNil(age) && gender && employer && yob && type) {
    return {
      lyra_id: userId,
      body: {
        age,
        gender,
        employer,
        yob,
        type,
        child_lyra_id: childUserId,
        productType,
        ...topicBody,
        ...subTopicBody,
        ...eligibilityTypeBody,
        ...(countryName && {
          region: {
            value: countryName,
          },
        }),
      },
    }
  } else {
    return {}
  }
}

/**
 * Extracts AI-detected risk level from metadata_gen_ai_responses
 */
export const getAiDetectedRiskLevel = (searchMetadataContext?: MetadataContext): TriageRiskLevel | undefined => {
  if (!searchMetadataContext) {
    return undefined
  }

  // Find any response with a highAlertRiskLevel
  const responseWithHighAlert = Object.values(searchMetadataContext).find(
    (response) => response?.gen_ai_response?.highAlertRiskLevel,
  )

  if (!responseWithHighAlert?.gen_ai_response?.highAlertRiskLevel) {
    return undefined
  }

  // Map server risk level format to TriageRiskLevel enum
  const riskLevelMap: { [key: string]: TriageRiskLevel } = {
    RISK_LEVEL_YELLOW: TriageRiskLevel.YELLOW,
    RISK_LEVEL_AMBER: TriageRiskLevel.AMBER,
    RISK_LEVEL_RED: TriageRiskLevel.RED,
  }

  return riskLevelMap[responseWithHighAlert.gen_ai_response.highAlertRiskLevel]
}

export const shouldShowHighRiskModal = (
  results: TriageResults,
  previousHighRiskModalLevelShown: TriageRiskLevel | undefined,
): boolean => {
  const traditionalRiskLevel = results.search?.risk_level || results.treatmentRecommendations?.riskLevel

  const aiRiskLevel = getAiDetectedRiskLevel(results.search?.metadata_context)

  const resultsRiskLevel = traditionalRiskLevel || aiRiskLevel

  if (!resultsRiskLevel) {
    return false
  }
  // show modal if first time presented
  if (!previousHighRiskModalLevelShown) {
    return true
  }
  // show modal if new risk level is higher than previous level
  return (
    previousHighRiskModalLevelShown === TriageRiskLevel.YELLOW &&
    (resultsRiskLevel === TriageRiskLevel.AMBER || resultsRiskLevel === TriageRiskLevel.RED)
  )
}

export const isActiveSI = (results: TriageResults) => {
  return (
    results.search?.suicidal_ideation_indicated || results.treatmentRecommendations?.suicidalIdeationIndicated || false
  )
}

/** Check for high risk searches and return the date of the most recent one, or null if none found.
 */
export const getMostRecentHighRiskSearchDateFromUserSearchHistory = ({ searches }: { searches: TriageSearch[] }) => {
  const highRiskSearch = searches.find(
    (search: TriageSearch) =>
      search.create_date &&
      (search.safety_alert_indicated || search.self_harm_indicated || search.suicidal_ideation_indicated),
  )
  if (highRiskSearch && highRiskSearch.create_date) return highRiskSearch.create_date
  return null
}

// Used to configure logic to bypass treatment recs page
// Returns a treatment option if skip conditions are met, this can be used to auto select a treatment option
export const getAutoSelectedTreatmentOption = (
  results: TriageResults,
  isCoachingOnly: boolean,
): TREATMENT_OPTIONS_TYPE | undefined => {
  let treatmentOption: TREATMENT_OPTIONS_TYPE | undefined = undefined
  const treatmentRecs = results.treatmentRecommendations
  if (!treatmentRecs) {
    return undefined
  }

  // Program aware triage rules
  const programAwareConditions =
    results.search?.body.programPreference !== undefined &&
    treatmentRecs.shownTierPage === TREATMENT_RECOMMENDATION_TIER.TIER_1

  if (
    (programAwareConditions || isCoachingOnly) &&
    treatmentRecs.primaryTreatmentOptions.filter((option) => tierOnePrimaryTreatmentOptionsToDisplay.includes(option))
      .length === 1 &&
    treatmentRecs.secondaryTreatmentOptions.filter((option) =>
      tierOneSecondaryTreatmentOptionsToDisplay.includes(option),
    ).length === 0 &&
    treatmentRecs.primaryTreatmentOptions[0] === TREATMENT_OPTIONS.COACHING
  ) {
    treatmentOption = TREATMENT_OPTIONS.COACHING
  }

  return treatmentOption
}

// Given a triage treatment option name, return the corresponding customer program.
// Note that we do not match treatment options to customer programs when the customer program is marked as "not a full
// treatment option". This means that the customer program is provided as part of another treatment option.
// An example of this is blended care therapy, which is provided as part of individual therapy. All blended care therapists
// are categorized under individual therapy, but not all therapists under individual therapy are part of blended care.
export const convertTriageTreatmentOptionToProgram = (
  treatmentOptionName: TREATMENT_OPTIONS_TYPE | null,
  allowPartialTreatmentOptions: boolean = false,
): ProgramConfig | undefined => {
  return Programs.getProgramConfigsMatching(
    (program) =>
      !!treatmentOptionName &&
      !!program.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(treatmentOptionName) &&
      (program.treatmentOptionProperties.isFullTreatmentOption || allowPartialTreatmentOptions),
  )[0]
}

export const convertTriageTaxonomyOptionToProgram = (
  programTaxonomy: ProviderProgramTaxonomy,
): ProgramConfig | undefined => {
  return Programs.getProgramConfigsMatching(
    (program) =>
      !!program.treatmentOptionProperties?.isFullTreatmentOption &&
      !!program.treatmentOptionProperties?.programTaxonomyTreatmentOptions?.find(
        (treatmentOption) =>
          treatmentOption.clientele === programTaxonomy.clientele &&
          treatmentOption.treatment === programTaxonomy.treatment &&
          treatmentOption.partner === programTaxonomy.partner &&
          treatmentOption.offering === programTaxonomy.offering,
      ),
  )[0]
}

/** Function that returns the BC program name associated with the selected treatment option.
 *
 *  Will return undefined if the selected treatment option is not a blended care program or if it is
 *  not included in any associated triage treatment options.
 *
 * TODO: This function depends on the order of program configs, which should not be relied on.
 * A given treatment option can map to multiple programs!
 */
export const getBCProgramNameFromSelectedTreatmentOption = (
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramNames | undefined => {
  const matchingProgram = Programs.getProgramConfigsMatching((program) => {
    const programTaxonomyOptions = program.treatmentOptionProperties?.programTaxonomyTreatmentOptions
    return (
      !!program.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(selectedTreatmentOption) &&
      !!programTaxonomyOptions &&
      isBlendedCareProvider({ programTaxonomy: programTaxonomyOptions })
    )
  })[0]
  return matchingProgram?.programName
}

const getProgramNameFromSelectedTreatmentOptionForCapacity = (
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramNames | 'DirectAccess' => {
  const matchingPrograms = Programs.getProgramConfigsMatching(
    (program) =>
      !!program.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(selectedTreatmentOption),
  )

  for (const program of matchingPrograms) {
    if (
      [ProgramNames.DIRECT_ACCESS_THERAPY, ProgramNames.ICASTherapy, ProgramNames.DIRECT_ACCESS_MEDS].includes(
        program.programName,
      )
    ) {
      return 'DirectAccess'
    }
  }

  return matchingPrograms[0]?.programName ?? ''
}

// Takes the categorized word cloud elements returned by the server and flattens it
// so we can send it along in searches to provide context for which words the user saw.
export const formatWordCloudForSearchContext = (wordCloud?: WordCloudData) => {
  const wordList = wordCloud
    ? wordCloud.reduce((acc: any, value: WordCloudCategory) => {
        const defaultMessages = value.word_cloud_elements.map((element) => element.defaultMessage)
        return acc.concat(defaultMessages)
      }, [])
    : []

  return { displayed_wordclouds: wordList }
}

export type CleanCompletedSearchForDuplicationArgs = {
  search: TriageSearch
  wordCloudWords: WordCloudWords | null
  getWordCloudWords: (data: GetWordCloudWordsActionData) => Promise<any>
  fetchedMetadataVersion?: string
} & Omit<GetWordCloudWordsActionData, 'triageScreenerName'>

// Takes a completed search and removes values so that we can use it as a request
// for a new POST to /search - effectively duplicating it
export const cleanCompletedSearchForDuplication = async ({
  search,
  wordCloudWords,
  getWordCloudWords,
  memberAge,
  primaryNeedsWord,
  country,
  lyraSocialCareEnabled,
  searcherType,
  fetchedMetadataVersion,
}: CleanCompletedSearchForDuplicationArgs) => {
  let context = {}
  const newSearchBody = omit(search.body, ['clinicalFactors', 'searchId'])
  const cleanedSearch = pick(search, ['triage_metadata', 'lyra_id', 'metadata_version']) as Dict
  newSearchBody.age = memberAge
  cleanedSearch.metadata_version = fetchedMetadataVersion

  // Add wordcloud context to search
  const wordCloudScreeners = [TriageScreenerNames.WORD_CLOUD_1, TriageScreenerNames.WORD_CLOUD_2]
  const wordCloudsNeedingContext = intersection(wordCloudScreeners, Object.keys(search.body.questionnaires || {}))
  const [wordCloudsInState, wordCloudsToFetch] = partition(
    wordCloudsNeedingContext,
    (screenerName) => wordCloudWords?.[screenerName],
  )

  // If word cloud date exists in state use that for the context
  if (wordCloudsInState.length && wordCloudWords) {
    context = wordCloudsInState.reduce(
      (acc, screenerName) => ({
        ...acc,
        [screenerName]: formatWordCloudForSearchContext(wordCloudWords[screenerName]),
      }),
      context,
    )
  }

  // If it's not in state then we need to fetch it from the server
  if (wordCloudsToFetch.length && memberAge && primaryNeedsWord) {
    const promises = wordCloudsToFetch.map((screenerName) =>
      getWordCloudWords({
        memberAge,
        searcherType,
        primaryNeedsWord,
        triageScreenerName: screenerName as TriageScreenerNames,
        country,
        lyraSocialCareEnabled,
      }),
    )
    try {
      const values = await Promise.all(promises)
      context = values.reduce(
        (acc, value) => ({
          ...acc,
          [value.screenerName || value.meta.arg.triageScreenerName]: formatWordCloudForSearchContext(
            value.response || value.payload,
          ),
        }),
        context,
      )
    } catch (error) {
      console.warn(error)
    }
  }

  context = { ...context, ...search.metadata_context }

  return { ...cleanedSearch, body: newSearchBody, context }
}

export const getProgramConfigByProvider = (provider: ProviderInfo): ProgramConfig | undefined => {
  const lyraType = provider?.lyra_type?.toUpperCase?.()
  if (!lyraType) {
    return undefined
  }
  const providerType: ProviderType | undefined = PROVIDER_TYPES[lyraType as keyof typeof PROVIDER_TYPES]
  return Programs.getProgramConfigByName(providerType?.program)
}

export const isWordCloud2Supported = (searcherType?: SearcherType, primaryNeed?: PrimaryNeedsWords) =>
  searcherType === SearcherType.ADULT ||
  (searcherType &&
    primaryNeed &&
    TriageScreenerNames.WORD_CLOUD_2 in (allChildPrimaryNeedsWordCloudMappings[searcherType][primaryNeed] ?? {}))

export const getNumberOfScreeners = (searcherType?: SearcherType, primaryNeed?: PrimaryNeedsWords) => {
  const selectedCurrentToNextMetadataNames = searcherType
    ? currentToNextMetadataNamesAll[searcherType]
    : currentToNextMetadataNamesDefault
  const screenersWithoutWordCloud2 = omit(selectedCurrentToNextMetadataNames, ['wordCloud2'])
  // E.g. Younger children and middle children do not have wordCloud2.
  const includesWordCloud2 = searcherType && primaryNeed ? isWordCloud2Supported(searcherType, primaryNeed) : true

  return includesWordCloud2
    ? Object.entries(selectedCurrentToNextMetadataNames).length
    : Object.entries(screenersWithoutWordCloud2).length
}

export const isMinor = (dob: string) => (dob ? moment().diff(moment(dob, 'MM/DD/YYYY'), 'years') < 18 : false)

export const isTreatmentOptionForAdultTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY
  )
}

export const isTreatmentOptionForChildTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD
  )
}

// Checks if the treatment option is any type of therapy (adult, child, or specialized)
export const isTreatmentOptionTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS ||
    treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.ONSITE_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER
  )
}

// Checks if the treatment option is any type of coaching
export const isTreatmentOptionCoaching = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.COACHING ||
    treatmentOption === TREATMENT_OPTIONS.COACHING_FOR_PARENTS ||
    treatmentOption === TREATMENT_OPTIONS.LIVE_MESSAGING_COACHING
  )
}

/**
 * Determine whether we should show the zero search results recommended treatment option page based on the treatment options.
 * Currently, only coaching is supported as a recommended treatment option.
 */
export const getHasCoachingAsEligibleTreatmentOption = ({
  treatmentOptions,
}: {
  treatmentOptions?: TREATMENT_OPTIONS_TYPE[]
}) => {
  if (!treatmentOptions) {
    return false
  }
  return treatmentOptions.some(isTreatmentOptionCoaching)
}

/**
 * Determines if the member preferences entry point section should be shown on provider results page
 * based on customer, user location, search path, and treatment option
 */
export const getShouldShowMemberPreferencesEntryPointSection = ({
  customerName,
  isUserInternational,
  isDirectPath,
  selectedTreatmentOption,
}: {
  customerName: string
  isUserInternational: boolean
  isDirectPath: boolean
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null
}): boolean => {
  const isTherapyOrCoaching =
    isTreatmentOptionTherapy(selectedTreatmentOption) || isTreatmentOptionCoaching(selectedTreatmentOption)

  return !isUserInternational && customerName !== CustomerName.STARBUCKS && !isDirectPath && isTherapyOrCoaching
}

export const getContactCareNavMessageWithNeurodiversityInfo = ({
  contactCareNavigatorFormMessage,
  neurodiversitySearcherInfo,
  toolkitSubTopic,
  primaryNeed,
}: {
  contactCareNavigatorFormMessage: string
  neurodiversitySearcherInfo: NeurodiversitySearcherInfo
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  primaryNeed?: PrimaryNeedsWords
}) => {
  const { contactCareNavEntryPoint, searcherType } = neurodiversitySearcherInfo
  const searcherTypeString = `For ${
    searcherType === NeurodiversitySearcherType.CHILD
      ? ContactCareNavMessageSearcherType.CHILD
      : ContactCareNavMessageSearcherType.SELF
  }`
  const getCaseInfo = () => {
    if (contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.NEURODIVERSITY_NAVIGATION) {
      return contactCareNavEntryPoint
    } else if (
      contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.CHILD_SEARCH_FOR_CARE &&
      primaryNeed
    ) {
      return `search for care - ${primaryNeed}`
    } else if (
      contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.SELECTED_CARE_OPTION &&
      toolkitSubTopic
    ) {
      return getToolkitSubTopicLabel(toolkitSubTopic)
    }
    return ''
  }
  return `${searcherTypeString} - ${getCaseInfo()}\n\n${contactCareNavigatorFormMessage}`
}

export const generatePostChildConcernContactPayload = ({
  user,
  customer,
  formValues,
  childLyraId,
  searchId,
  typeOfRequest,
}: {
  user: User
  customer: Customer
  formValues: ContactUsFormValues
  childLyraId?: string
  searchId?: string
  typeOfRequest?: string
}) => ({
  firstname: user?.firstname,
  lastname: user?.lastname,
  note: formValues.textareanote,
  careManagerName: customer?.defaultCareManager?.firstname || '',
  careManagerEmail: customer?.defaultCareManager?.username || '',
  call: formValues.phoneNumber,
  email: formValues.emailAddress || '',
  customerName: customer?.name || 'care',
  childLyraId,
  searchId,
  typeOfRequest,
})

export const generateContactUsFormValuesFromUserInfo = ({
  userContactPreference,
  userEmail,
  userPhone,
  contactCareNavigatorFormMessage,
}: {
  userContactPreference?: string
  userEmail?: string
  userPhone?: string | null
  contactCareNavigatorFormMessage?: string
}) => {
  const userPrefersEmail =
    isNil(userContactPreference) ||
    userContactPreference === ContactPref.UNASSIGNED ||
    userContactPreference === ContactPref.EMAIL
  const contactCareInfo: ContactUsFormValues = {
    emailChecked: userPrefersEmail,
    phoneChecked: !userPrefersEmail,
    emailAddress: !isNil(userEmail) ? userEmail : '',
    // don't include phone number if it isn't needed
    phoneNumber: !isNil(userPhone) && !userPrefersEmail ? userPhone : '',
    textareanote: '\n\n' + contactCareNavigatorFormMessage + '\n',
  }
  return contactCareInfo
}

export const getUnsupportedTreatmentOptionsConfiguration = ({
  unsupportedTreatmentOptionsConfigurations,
  excludedTreatmentOptionsReasons,
  isUserInternational,
}: {
  unsupportedTreatmentOptionsConfigurations: UnsupportedTreatmentOptionsConfigurationsType
  excludedTreatmentOptionsReasons?: ExcludedReason[]
  isUserInternational: boolean
}) => {
  if (isUserInternational || !excludedTreatmentOptionsReasons) {
    return null
  }
  const nonClinicalExclusionReasons = [
    'customerDisallowsSelfCareApps',
    'customerDisallowsStressManagement',
    'customerDisallowsStressManagementLM',
    'customerDisallowsSingleSessionCoaching',
    'customerDisallowsBCTherapy',
    'customerDisallowsDATherapy',
    'customerDisallowsBCMeds',
    'customerDisallowsDAMeds',
    'userBCMedsEligible', // When both BC and DA meds are eligible option, we remove DA meds
    'coachesUnavailable',
    'minorTreatmentTypeReplacedWithAdultType',
    'icasProvidersUnavailable', // excludes individualTherapy for eligible international users
    'icasProvidersAvailable', // excludes individualTherapyICASPhone for eligible international users
    'memberOUSAndNonEnglishDisplayLanguage', // excludes Coaching if member OUS and non-English display language
  ]

  const isExcludedForNonClinicalReasons = (optionToCheck: $TSFixMe) => {
    const excludedOption = excludedTreatmentOptionsReasons.find(
      (option: $TSFixMe) => option.treatmentOption === optionToCheck,
    )
    if (excludedOption) {
      const allReasonsAreNonClinical = excludedOption.reasons.every((reason: $TSFixMe) =>
        nonClinicalExclusionReasons.includes(reason),
      )
      const isNotAllowedByCustomer = excludedOption.reasons.some((reason: $TSFixMe) =>
        reason.startsWith('customerDisallows'),
      )
      return allReasonsAreNonClinical && isNotAllowedByCustomer
    } else {
      return false
    }
  }

  const medsDAChildExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD)
  const medsDAExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS)
  const medsBCExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.BLENDED_CARE_MEDS)
  const individualTherapyChildExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD)
  const individualTherapyExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.INDIVIDUAL_THERAPY)

  if (medsDAChildExcluded && medsDAExcluded && individualTherapyChildExcluded && individualTherapyExcluded) {
    return unsupportedTreatmentOptionsConfigurations.combined
  } else if (medsDAChildExcluded && medsDAExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.meds
  } else if (medsDAChildExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.medsChild
  } else if (medsDAExcluded && medsBCExcluded && individualTherapyExcluded) {
    return unsupportedTreatmentOptionsConfigurations.combined
  } else if (medsDAExcluded && medsBCExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.meds
  } else {
    return null
  }
}

// if the Child Search - Direct to CN feature flag is enabled, and
// the search is for child, then
// replace the PRIMARY_NEEDS metadata with the PRIMARY_NEEDS_DIRECT_CN metadata
export const usePrimaryNeedsDirectCNMetadata = (
  shouldShowChildDirectPathToCareNavigator: boolean,
  triageMetadata: TriageScreenerType[],
) => {
  let newMetadata = cloneDeep(triageMetadata)
  if (shouldShowChildDirectPathToCareNavigator) {
    const primaryNeedsID = newMetadata.find((metadata) => metadata.name === TriageScreenerNames.PRIMARY_NEEDS)
      ?.id as string
    newMetadata = newMetadata
      .filter((metadata) => metadata.name !== TriageScreenerNames.PRIMARY_NEEDS)
      .map((metadata) =>
        metadata.name === TriageScreenerNames.PRIMARY_NEEDS_DIRECT_CN
          ? { ...metadata, name: TriageScreenerNames.PRIMARY_NEEDS, id: primaryNeedsID }
          : metadata,
      )
  }
  return newMetadata
}

/**
 * Internal function to check if a provider has capacity for any program.
 * Used in the direct booking case to ensure that providers are correctly shown as available.
 */
const checkProviderHasAnyCapacity = ({
  providerCapacity,
  isDirectBooking = false,
}: {
  providerCapacity?: ProviderCapacity
  isDirectBooking?: boolean
}) => {
  if (!isDirectBooking || !providerCapacity) {
    return false
  }

  return sum(Object.values(providerCapacity)) > 0
}

/**
 * Function to help check if a provider has capacity for a given treatment option.
 * Supports checking LCx and DA providers and handling legacy providers that do not have a providerCapacity object
 */
export const checkProviderCapacity = ({
  providerCapacity,
  selectedTreatmentOption,
  isDirectBooking,
}: {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  isDirectBooking?: boolean
}) => {
  const legacyProviderCapacity = providerCapacity === undefined
  const program = getProgramNameFromSelectedTreatmentOptionForCapacity(selectedTreatmentOption)

  const hasCapacityForDirectBooking = checkProviderHasAnyCapacity({ providerCapacity, isDirectBooking })

  const hasCapacityForGivenProgram = providerCapacity?.[program] !== 0
  /**
   * Providers that do not have a providerCapacity object returned are legacy providers or bad
   * data providers that should not have been surfaced, but are being surfaced by Arbiter/PSS
   * These providers have a null value for providerCapacity in LyraTherapy UPS Database. On the front end we get undefined.
   * We should default these providers to always having capacity for backwards compatibility
   *
   * On Production we should always expect providerCapacity to be defined by provider results
   * and we should verify that the capacity value is non-zero for the program.
   */

  if (isDirectBooking) {
    const queryParams = new URLSearchParams(window?.location?.search)
    // If present, the `limit` parameter designates when a direct booking link was generated by a provider.
    // We allow bookings if `limit` is present AND if it represents a time no more than 72 hours ago.
    // This intentionally bypasses capacity to allow certain clients to book as vetted by the provider.
    const limit = queryParams.get('limit')
    let isLimitWithin72HoursInThePast = false
    if (limit) {
      const limitInDate = new Date(atob(limit))
      isLimitWithin72HoursInThePast = isWithinXHoursInThePast(limitInDate, 72)
    }
    return legacyProviderCapacity || hasCapacityForDirectBooking || isLimitWithin72HoursInThePast
  }
  return legacyProviderCapacity || hasCapacityForGivenProgram || hasCapacityForDirectBooking
}

export const isShowAppointmentsDisabled = ({
  providerCapacity,
  selectedTreatmentOption,
  hasAvailability,
  hasConnectedCalendar,
  hasCalendarError,
  isDirectBooking,
  providerMissingForSearch,
}: {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  hasAvailability: boolean
  hasConnectedCalendar: boolean
  hasCalendarError?: boolean
  isDirectBooking?: boolean
  providerMissingForSearch?: boolean
}) => {
  const hasCapacity = checkProviderCapacity({
    providerCapacity,
    selectedTreatmentOption,
    isDirectBooking,
  })
  /**
   * A provider that does not have capacity or availability should not be allowed
   * to be booked.
   *
   * A provider with an error should not be allowed to be booked
   */
  return Boolean(
    (hasConnectedCalendar && (!hasCapacity || !hasAvailability || hasCalendarError)) || providerMissingForSearch,
  )
}

export const isContactForAvailabilityDisabled = ({
  providerCapacity,
  selectedTreatmentOption,
  isDirectBooking,
}: {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  isDirectBooking?: boolean
}) => {
  const hasCapacity = checkProviderCapacity({
    providerCapacity,
    selectedTreatmentOption,
    isDirectBooking,
  })
  return !hasCapacity && !isDirectBooking
}

export const getInPersonProvidersCount = ({ providers }: { providers: ProviderInfo[] }) => {
  const inPersonProviderCount = providers.reduce((providerCount, currentProvider) => {
    const supportsInPerson = getProviderSupportsInPerson({
      treatmentMode: TreatmentModeEnums[currentProvider.supportedVisitTypes],
    })

    return supportsInPerson ? providerCount + 1 : providerCount
  }, 0)

  return inPersonProviderCount
}

/**
 * Checks if the user is a teen searching for care between the ages of 13-17
 */
export const isSelfInitiatedTeenSearchingForCare = ({
  isSearchingForAdultTherapy,
  searcherDOB,
}: {
  isSearchingForAdultTherapy: boolean
  searcherDOB: string
}): boolean => {
  const age = getAge({ date: searcherDOB })
  return isSearchingForAdultTherapy && isAgeTeen(age)
}

/**
 * Returns current search for care flow of the user
 */
export const getSearchForCareProgramOptionFlow = ({
  isSearchingForAdultTherapy,
  isSearchingForCoaching,
  searcherDOB,
  isSearchingForChildTherapy,
  isLMSCoachingEligible,
}: {
  isSearchingForAdultTherapy: boolean
  isSearchingForCoaching: boolean
  searcherDOB: string
  isSearchingForChildTherapy: boolean
  isLMSCoachingEligible?: boolean
}): S4C_PROGRAM_OPTION_FLOW => {
  const isSelfInitiatedTeen = isSelfInitiatedTeenSearchingForCare({ isSearchingForAdultTherapy, searcherDOB })
  const isSeacherTeen = isAgeTeen(getAge({ date: searcherDOB }))
  if (isSearchingForChildTherapy && isSeacherTeen) {
    return S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN
  } else if (isSelfInitiatedTeen) {
    return S4C_PROGRAM_OPTION_FLOW.SELF_INITIATED_TEENS
  } else if (isSearchingForAdultTherapy) {
    return S4C_PROGRAM_OPTION_FLOW.THERAPY
  } else if (isLMSCoachingEligible && isSearchingForCoaching) {
    return S4C_PROGRAM_OPTION_FLOW.COACHING_WITH_LMS
  } else if (isSearchingForCoaching) {
    return S4C_PROGRAM_OPTION_FLOW.COACHING
  } else if (isSearchingForChildTherapy) {
    return S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD
  }

  return S4C_PROGRAM_OPTION_FLOW.OTHER
}

export const getPrimaryNeedAndWordCloudExperiences = (search?: TriageSearch) => {
  const questionnaires = search?.body?.questionnaires
  const result = []
  if (questionnaires) {
    // Single select primaryNeed
    const whatsOnYourMindValue = (questionnaires as $TSFixMe)?.[TriageScreenerNames.PRIMARY_NEEDS]?.[
      TriageScreenerField.WHATS_ON_YOUR_MIND
    ]
    // Multiselect primaryNeed
    const selectionsValue = (questionnaires as $TSFixMe)?.[TriageScreenerNames.PRIMARY_NEEDS]?.[
      TriageScreenerField.SELECTIONS
    ]
    if (whatsOnYourMindValue) {
      result.push(whatsOnYourMindValue)
    }
    if (selectionsValue) {
      selectionsValue.forEach((value: string) => {
        result.push(value)
      })
    }
    if (
      (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_1]?.[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING]
    ) {
      result.push(
        (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_1]?.[
          TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING
        ],
      )
    }
    if (
      (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_2]?.[
        TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2
      ]
    ) {
      result.push(
        (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_2]?.[
          TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2
        ],
      )
    }
  }
  if (search?.body.clientSelectedTopic) {
    if (search?.body.clientSelectedSubTopic) {
      result.push(search.body.clientSelectedSubTopic)
    } else {
      result.push(search.body.clientSelectedTopic)
    }
  }
  return uniq(result.flat().sort())
}

// Convert primary needs (adult and child) and triage words to their translated message
export const convertPrimaryAndTriageTermsToMessages = (word: string): MessageDescriptor | undefined => {
  // Try to use the mapping key first for a primary need and triage term then use the string as-is
  let mappingWord = word
  // Check if the word exists in the mapping dictionary
  if (word in PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG) {
    mappingWord =
      PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG[
        word as keyof typeof PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG
      ]
  }
  // Check if mappingWord exists in any of the message dictionaries
  if (mappingWord in PRIMARY_NEEDS_WORDS_ADULT_MSG) {
    return PRIMARY_NEEDS_WORDS_ADULT_MSG[mappingWord as keyof typeof PRIMARY_NEEDS_WORDS_ADULT_MSG]
  }
  if (mappingWord in PRIMARY_NEEDS_WORDS_CHILD_MSG) {
    return PRIMARY_NEEDS_WORDS_CHILD_MSG[mappingWord as keyof typeof PRIMARY_NEEDS_WORDS_CHILD_MSG]
  }
  if (mappingWord in TRIAGE_WORDS_MSG) {
    return TRIAGE_WORDS_MSG[mappingWord as keyof typeof TRIAGE_WORDS_MSG]
  }
  const toolkitTopicDescriptionMsgs = getToolkitTopicDescriptions()
  const mappingWordTopic = getTopicFromClientSelectedTopicKey(mappingWord)
  if (mappingWordTopic in toolkitTopicDescriptionMsgs) {
    return toolkitTopicDescriptionMsgs[mappingWordTopic as keyof typeof toolkitTopicDescriptionMsgs]
  }
  const toolkitSubTopicDescriptionsMsgs = getToolkitSubTopicDescriptions()
  const mappingWordSubTopic = getTopicFromClientSelectedSubTopicKey(mappingWord)
  if (mappingWordSubTopic in toolkitSubTopicDescriptionsMsgs) {
    return toolkitSubTopicDescriptionsMsgs[mappingWordSubTopic as keyof typeof toolkitSubTopicDescriptionsMsgs]
  }

  return undefined
}

export const convertPrimaryAndTriageTermsToMessagesArr = (strArr: string[]): Array<MessageDescriptor> => {
  return strArr
    .map((word) => convertPrimaryAndTriageTermsToMessages(word))
    .filter((descriptor): descriptor is MessageDescriptor => descriptor !== undefined)
}

export const isChildSearch = (search: TriageSearch) => {
  return Boolean(search.body?.childLyraId || search.body?.child_lyra_id)
}

export const getTriageSummaryType = (age?: number, searcherType?: SearcherType): SearcherForTriageSummary => {
  if (isNumber(age) && !isNil(SearcherForTriageSummary)) {
    if (isAgeYoungerThanTeen(age)) {
      return SearcherForTriageSummary.CHILD
    } else if (isAgeTeen(age) && searcherType !== SearcherType.ADULT) {
      return SearcherForTriageSummary.TEEN
    } else {
      return SearcherForTriageSummary.ADULT
    }
  } else {
    return SearcherForTriageSummary.ADULT
  }
}

export const getSpeculativeSearchDefaultCoachingSearchParams = ({
  isInternationalUser,
  userCountryName,
  userDOB,
  isSpeculativeFilterEnabled,
}: {
  isInternationalUser: boolean
  userCountryName?: string
  userDOB?: string
  isSpeculativeFilterEnabled: boolean
}) => {
  const defaultSearchParams: SearchRequestData = {
    isUserInternational: isInternationalUser,
    onsite: false,
    matchFor: 'self',
    locationCountry: userCountryName,
    treatmentNaive: TREATMENT_OPTIONS.COACHING,
    treatmentPreference: TREATMENT_OPTIONS.COACHING,
    ...(isSpeculativeFilterEnabled && {
      ...DEFAULT_SPECULATIVE_FILTER_THRESHOLDS[WELLNESS_TOOLKIT_TYPE.INDIVIDUAL_COACHING],
    }),
  }

  defaultSearchParams.age = getAge({ date: userDOB ?? '' })

  return defaultSearchParams
}

export const getShowProviderResultsSurveyUsingDateLastSeen = ({
  providerResultsSurveyDateLastSeen,
}: {
  providerResultsSurveyDateLastSeen?: string
}) => {
  let showSurvey = false

  if (providerResultsSurveyDateLastSeen) {
    const dateThreeMonthsAhead = addMonths(
      parseISO(providerResultsSurveyDateLastSeen),
      NUMBER_OF_MONTHS_UNTIL_SURVEY_CAN_BE_SHOWN_AGAIN,
    )
    if (isBefore(new Date(), dateThreeMonthsAhead)) {
      showSurvey = false
    } else {
      showSurvey = true
    }
  } else {
    showSurvey = true
  }

  return showSurvey
}

/** Append 'topic' at the end of the key to make sure the value doesn't conflict with other keys in the triage flow. For example if the stress toolkit key was 'stress' it can conflict with the 'stress' word cloud option */
export const getClientSelectedTopicKey = (topic?: string) => {
  if (!topic) {
    return ''
  }
  return `${topic}-topic`
}

export const getTopicFromClientSelectedTopicKey = (key: string) => {
  if (key.endsWith('-topic')) {
    return key.slice(0, -6)
  }
  return key
}

export const getClientSelectedSubTopicKey = (subTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC): string => {
  if (!subTopic) {
    return ''
  }
  return `${subTopic}${WELLNESS_SUB_TOPIC_SUFFIX}`
}

export const getTopicFromClientSelectedSubTopicKey = (key: string) => {
  if (key.endsWith(WELLNESS_SUB_TOPIC_SUFFIX)) {
    return key.replace(WELLNESS_SUB_TOPIC_SUFFIX, '')
  }
  return key
}

export const shouldSkipPrimaryNeeds = ({
  isSearchProgramAware,
  isSearchToolkitAware,
  isCOEFlow,
}: {
  isSearchProgramAware?: boolean
  isSearchToolkitAware?: boolean
  isCOEFlow: boolean
}) => {
  return isSearchToolkitAware || isSearchProgramAware || isCOEFlow ? TriageScreenerNames.PRIMARY_NEEDS : undefined
}

/**
 * Mapping of GenAI dynamic variables to their corresponding data sources
 */
export interface GenAiVariableData {
  employer?: string
  client_first_name?: string
}

/**
 * Replaces dynamic variables in GenAI response text with actual user/customer data.
 * Dynamic variables are in the format {{variableName}}
 * Example:
 * - Input: "Lyra is a benefit provided to you by {{employer}}"
 * - Output: "Lyra is a benefit provided to you by eBay"
 * @param text - The GenAI response text containing dynamic variables
 * @param data - Object containing the variable values to inject
 * @returns The text with all dynamic variables replaced with actual values
 */
export const injectGenAiVariables = (
  text: string | null | undefined,
  data: GenAiVariableData,
): string | null | undefined => {
  if (!text) {
    return text
  }

  // Replace all {{variableName}} with the corresponding value from data
  return text.replaceAll(/\{\{(\w+)\}\}/g, (match, variableName) => {
    const value = data[variableName as keyof GenAiVariableData]
    return value !== undefined ? value : match
  })
}

export interface SkipGenAIParams {
  submittedMetadataName: string
  displayMetadataName?: string
  submittedValues: Record<string, any>
  allQuestionnaires: Record<string, any>
  highAlertRiskLevel?: string
}

export const shouldSkipGenAI = ({
  submittedMetadataName,
  submittedValues,
  allQuestionnaires,
  highAlertRiskLevel,
}: SkipGenAIParams): boolean => {
  // Skip GenAI if high alert risk level is detected in AI freetext
  if (highAlertRiskLevel) {
    return true
  }
  // Use submittedValues for the screener that was just submitted, otherwise use stored data
  const primaryNeedsData =
    submittedMetadataName === TriageScreenerNames.PRIMARY_NEEDS
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.PRIMARY_NEEDS] || {}
  const wordCloud1Data =
    submittedMetadataName === TriageScreenerNames.WORD_CLOUD_1
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.WORD_CLOUD_1] || {}
  const wordCloud2Data =
    submittedMetadataName === TriageScreenerNames.WORD_CLOUD_2
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.WORD_CLOUD_2] || {}
  const intensityData =
    submittedMetadataName === TriageScreenerNames.INTENSITY
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.INTENSITY] || {}

  const selections = primaryNeedsData[TriageScreenerField.SELECTIONS] || []
  const whatsOnYourMind = primaryNeedsData[TriageScreenerField.WHATS_ON_YOUR_MIND]
  const whatElseOnYourMind = primaryNeedsData[TriageScreenerField.WHAT_ELSE_ON_YOUR_MIND]
  const whatAreYouExperiencing = wordCloud1Data[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING] || []
  const whatAreYouExperiencing2 = wordCloud2Data[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2] || []
  const selfHarmSliderValue = intensityData[TriageWords.SELF_HARM]

  // Skip GenAI if no primary needs selected or only "Something else" without free text
  if (submittedMetadataName === TriageScreenerNames.PRIMARY_NEEDS) {
    const hasNoFreeText = !whatElseOnYourMind || whatElseOnYourMind.trim() === ''
    const hasNoOtherPrimaryNeeds = Array.isArray(selections)
      ? selections.filter((s) => s !== TriageScreenerValue.SOMETHING_ELSE).length === 0
      : whatsOnYourMind === TriageScreenerValue.SOMETHING_ELSE || !whatsOnYourMind

    if (hasNoFreeText && hasNoOtherPrimaryNeeds) {
      return true
    }
  }

  // Skip GenAI if no word cloud topics selected or if "Self-harm" is selected
  if (submittedMetadataName === TriageScreenerNames.WORD_CLOUD_1) {
    const selectionsArray = (
      Array.isArray(whatAreYouExperiencing) ? whatAreYouExperiencing : [whatAreYouExperiencing].filter(Boolean)
    ) as string[]

    if (selectionsArray.length === 0) {
      return true
    }

    if (selectionsArray.includes(TriageWords.SELF_HARM)) {
      return true
    }
  }

  if (submittedMetadataName === TriageScreenerNames.WORD_CLOUD_2) {
    const wc1Selections = (
      Array.isArray(whatAreYouExperiencing) ? whatAreYouExperiencing : [whatAreYouExperiencing].filter(Boolean)
    ) as string[]
    const wc2Selections = (
      Array.isArray(whatAreYouExperiencing2) ? whatAreYouExperiencing2 : [whatAreYouExperiencing2].filter(Boolean)
    ) as string[]
    const combinedSelections = [...wc1Selections, ...wc2Selections]

    if (combinedSelections.length === 0) {
      return true
    }

    if (combinedSelections.includes(TriageWords.SELF_HARM)) {
      return true
    }
  }

  // Skip GenAI if "Self-harm" slider submitted or only nonSpecifiedPrimaryNeed slider present
  if (submittedMetadataName === TriageScreenerNames.INTENSITY) {
    const intensityKeys = Object.keys(intensityData || {})
    const sliderKeys = intensityKeys.filter((key) => key !== 'metadataId')
    const hasOnlyNonSpecifiedPrimaryNeedSlider =
      sliderKeys.length === 1 && sliderKeys[0] === TriageScreenerField.NON_SPECIFIED_PRIMARY_NEED

    if (hasOnlyNonSpecifiedPrimaryNeedSlider) {
      return true
    }

    if (selfHarmSliderValue !== undefined && selfHarmSliderValue !== null) {
      return true
    }
  }

  return false
}

export const getTriageSearchesWithinPastTimeFrame = (
  triageSearches: TriageSearch[],
  amount: number,
  unit: TimeUnit,
) => {
  return triageSearches.filter((search) => {
    if (!search.update_date) return false
    const searchDate = new Date(search.update_date)
    return isWithinPastTimeFrame(searchDate, amount, unit)
  })
}
